import {
  ArrowRight,
  BookOpen,
  Building2,
  Check,
  FileText,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
  Sunrise,
} from 'lucide-react';
import { researchProducts } from '../data/marketData.js';

const icons = {
  sunrise: Sunrise,
  file: FileText,
  building: Building2,
  sparkles: Sparkles,
  shield: ShieldCheck,
  book: BookOpen,
};

const plans = [
  {
    name: 'Free',
    price: '₹0',
    note: 'For market orientation',
    items: ['Market map & basic screener', '3 watchlists', '5 price alerts', 'Paper portfolio', 'Educational learning paths'],
    cta: 'Current plan',
  },
  {
    name: 'Pro',
    price: '₹399',
    suffix: '/ month',
    note: 'For self-directed investors',
    items: ['Unlimited screens & alerts', 'Morning and closing briefs', 'Filing and results monitor', 'Portfolio analytics', 'CSV/PDF exports'],
    cta: 'Join Pro waitlist',
    featured: true,
  },
  {
    name: 'Research',
    price: '₹999',
    suffix: '/ month',
    note: 'Only after RA registration',
    items: ['Everything in Pro', 'Documented stock research', 'IPO research notes', 'Model research baskets', 'Analyst webinars & updates'],
    cta: 'Coming after registration',
    locked: true,
  },
];

export default function ResearchHub() {
  return (
    <div className="page-stack research-page">
      <section className="research-hero">
        <div className="research-hero-copy">
          <span className="eyebrow"><Sparkles size={12} /> Product & revenue roadmap</span>
          <h1>From market noise to<br /><em>well-labeled insight.</em></h1>
          <p>Premium services are designed in compliance lanes: analytics now, regulated research after RA registration, and personal advice only after IA registration.</p>
          <div className="hero-actions">
            <button className="primary-button">Explore services <ArrowRight size={16} /></button>
            <a href="#regulatory-lanes" className="subtle-button">See service gates</a>
          </div>
        </div>
        <div className="research-principles">
          <span>THE VIZMARKETS STANDARD</span>
          <h2>Every insight should answer four questions.</h2>
          <ol>
            <li><strong>01</strong><span><b>What is the source?</b><small>Traceable filings and timestamped data.</small></span></li>
            <li><strong>02</strong><span><b>What is fact vs opinion?</b><small>Clear visual and editorial labels.</small></span></li>
            <li><strong>03</strong><span><b>What can go wrong?</b><small>Risks get equal prominence.</small></span></li>
            <li><strong>04</strong><span><b>Who is accountable?</b><small>Named author, disclosures and audit trail.</small></span></li>
          </ol>
        </div>
      </section>

      <section>
        <div className="section-title-row">
          <div><span className="eyebrow">Service catalogue</span><h2>Premium products people will return for</h2></div>
          <span className="policy-note"><ShieldCheck size={15} /> Gated by regulatory status</span>
        </div>
        <div className="product-grid">
          {researchProducts.map((product) => {
            const Icon = icons[product.icon] || FileText;
            const gated = product.status.includes('required');
            return (
              <article className="product-card" key={product.id}>
                <div className="product-card-top">
                  <span className="product-icon"><Icon size={20} /></span>
                  <span className={`status-chip ${gated ? 'gated' : ''}`}>{gated && <LockKeyhole size={11} />}{product.status}</span>
                </div>
                <span className="product-eyebrow">{product.eyebrow}</span>
                <h3>{product.title}</h3>
                <p>{product.description}</p>
                <footer><span>{product.cadence}</span><b>{product.tier}</b></footer>
              </article>
            );
          })}
        </div>
      </section>

      <section className="pricing-section">
        <div className="section-title-row">
          <div><span className="eyebrow">Suggested pricing</span><h2>A simple ladder from utility to trust</h2></div>
          <p>Validate willingness to pay before final pricing.</p>
        </div>
        <div className="plans-grid">
          {plans.map((plan) => (
            <article className={`plan-card ${plan.featured ? 'featured' : ''}`} key={plan.name}>
              {plan.featured && <span className="recommended-label">BEST STARTING POINT</span>}
              <span className="plan-name">{plan.name}</span>
              <h3>{plan.price} <small>{plan.suffix}</small></h3>
              <p>{plan.note}</p>
              <ul>{plan.items.map((item) => <li key={item}><Check size={15} /> {item}</li>)}</ul>
              <button disabled={plan.locked}>{plan.locked && <LockKeyhole size={14} />}{plan.cta}</button>
            </article>
          ))}
        </div>
        <p className="pricing-disclaimer">Illustrative commercial plan, not a live offer. Taxes, refund terms, customer support and final regulated-service pricing require legal and tax review.</p>
      </section>
    </div>
  );
}
